/**
 * Query Keys Constants
 * 
 * Centralized query keys for React Query to ensure consistency across the app.
 * This makes it easy to invalidate related queries after mutations.
 * 
 * Usage:
 * import { queryKeys } from '@/shared/utils/queryKeys';
 * 
 * // In useQuery
 * queryKey: queryKeys.dashboard.metrics()
 * 
 * // In useMutation
 * invalidateQueries: [queryKeys.dashboard.all()]
 */

export const queryKeys = {
  // Dashboard queries
  dashboard: {
    all: () => ['dashboard'],
    metrics: () => ['dashboard', 'metrics'],
    transactions: () => ['dashboard', 'transactions'],
  },

  // Wallet queries
  wallet: {
    all: () => ['wallet'],
    balance: () => ['wallet', 'balance'],
    transactions: () => ['wallet', 'transactions'],
    recipientSearch: (query) => ['wallet', 'recipient-search', query],
  },

  // Disbursement queries
  disbursements: {
    all: () => ['disbursements'],
    list: (startDate, endDate) => ['disbursements', 'list', startDate, endDate],
    detail: (id) => ['disbursements', 'detail', id],
    services: () => ['disbursements', 'services'],
    feeCalculation: (params) => ['disbursements', 'fee-calculation', params],
  },

  // Settings queries
  settings: {
    all: () => ['settings'],
    profile: () => ['settings', 'profile'],
    security: () => ['settings', 'security'],
    business: () => ['settings', 'business'],
    bankAccounts: () => ['settings', 'bank-accounts'],
    businessOwners: (businessId) => ['settings', 'business-owners', businessId],
    mobileMoneyAccounts: () => ['settings', 'mobile-money-accounts'],
    team: () => ['settings', 'team'],
    verification: () => ['settings', 'verification'],
    verificationLevel: (userType, businessId) => ['settings', 'verification-level', userType, businessId],
  },

  // Payment methods queries
  paymentMethods: {
    all: () => ['payment-methods'],
    bankAccounts: () => ['payment-methods', 'bank-accounts'],
    mobileMoneyAccounts: () => ['payment-methods', 'mobile-money-accounts'],
    settlementConfig: () => ['payment-methods', 'settlement-config'],
  },

  // Invoice queries
  invoices: {
    all: () => ['invoices'],
    list: () => ['invoices', 'list'],
    detail: (id) => ['invoices', 'detail', id],
    profile: () => ['invoices', 'profile'],
  },

  // Payroll queries
  payroll: {
    all: () => ['payroll'],
    batches: () => ['payroll', 'batches'],
  },

  // Reports queries
  reports: {
    all: () => ['reports'],
    list: (filters) => ['reports', 'list', filters],
  },

  // Utilities queries
  utilities: {
    all: () => ['utilities'],
    services: () => ['utilities', 'services'],
  },

  // Settlements queries
  settlements: {
    all: () => ['settlements'],
    report: () => ['settlements', 'report'],
    records: () => ['settlements', 'records'],
  },
};

export default queryKeys;

